import {Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {MinimalFolder, SearchedMinimalFolder} from 'constants/FolderConstants';
import notifications from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import SelectTree from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import {assertNever} from 'util/Types';

import * as FileSelectorUtil from './FileSelectorUtil';
import FolderDAO from './FolderDAO';
import usePaginatedFolderTree, {PaginatedFolderOptions} from './usePaginatedFolderTree';

/** The most matching folders a paginated search shows before asking the user to refine the filter. */
export const FOLDER_SEARCH_LIMIT = 100;

export interface FolderSelectTreeProps {
  allowNone?: boolean;
  className?: string;
  /** Folders created while the picker is open, shown immediately without refetching earlier pages. */
  createdFolders?: Array<MinimalFolder>;
  folderIds: Array<string>;
  onChangeSelectedFolders: (folderIds: Array<string>) => void;
  folders?: Array<MinimalFolder>;
  includeHiddenProjects?: boolean;
  multiple: boolean;
  paginated?: PaginatedFolderOptions;
  submitHandler?: () => void;
}

type FolderTreeMode = 'eager' | 'paginated-browse' | 'paginated-search';

function getFolderTreeMode(paginated: PaginatedFolderOptions | undefined, query: string): FolderTreeMode {
  if (paginated == null) {
    return 'eager';
  }
  return query === '' ? 'paginated-browse' : 'paginated-search';
}

function mergeTreeItems(...itemGroups: Array<Array<TreeItem>>): Array<TreeItem> {
  return [...new Map(itemGroups.flat().map((item) => [item.id, item])).values()];
}

function getCreatedFolderTreeItems(
  folders: Array<MinimalFolder>,
  createdFolders: Array<MinimalFolder>
): {ancestorItems: Array<TreeItem>; ancestorIds: ReadonlySet<string>; leafItems: Array<TreeItem>} {
  const createdFolderIds = new Set(createdFolders.map((folder) => folder.api_identifier));
  const ancestorIds = new Set<string>();
  for (const folder of createdFolders) {
    let parent = folder.parent;
    while (parent != null) {
      ancestorIds.add(parent.api_identifier);
      parent = parent.parent;
    }
  }
  const folderItems = FileSelectorUtil.foldersToSelectTreeItems(folders);
  return {
    ancestorItems: folderItems.filter((item) => ancestorIds.has(item.id)),
    ancestorIds,
    leafItems: folderItems.filter((item) => createdFolderIds.has(item.id) && !ancestorIds.has(item.id)),
  };
}

export default function FolderSelectTree({
  includeHiddenProjects = false,
  allowNone = false,
  ...props
}: FolderSelectTreeProps): JSX.Element {
  const [query, setQuery] = useState('');
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebounce(trimmedQuery, 500);
  const [searchResult, setSearchResult] = useState<{
    query: string;
    folders: Array<SearchedMinimalFolder>;
  } | null>(null);
  // Without the rollout flag, paginated search loads every folder once and filters it locally.
  const [completeFolderList, setCompleteFolderList] = useState<Array<MinimalFolder> | null>(null);
  const searchesOnServer = BenchlingConfig.PERMISSIONS_ENABLE_PAGINATED_FOLDER_SELECTOR;
  const paginatedLevel = props.paginated?.level;
  const showInventory = props.paginated?.showInventory ?? false;
  const showNotebook = props.paginated?.showNotebook ?? false;
  const onPaginatedFoldersLoaded = props.paginated?.onFoldersLoaded;

  // The modal validates the selection against every folder it has received, so it must keep both browsed
  // folders and search matches even as either list changes.
  const browsedFoldersRef = useRef<Array<MinimalFolder>>([]);
  const searchedFoldersByIdRef = useRef(new Map<string, MinimalFolder>());
  const publishLoadedFolders = useCallback((): void => {
    const browsedIds = new Set(browsedFoldersRef.current.map((folder) => folder.api_identifier));
    onPaginatedFoldersLoaded?.([
      ...browsedFoldersRef.current,
      ...[...searchedFoldersByIdRef.current.values()].filter(
        (folder) => !browsedIds.has(folder.api_identifier)
      ),
    ]);
  }, [onPaginatedFoldersLoaded]);
  const paginatedOptions = useMemo(
    (): PaginatedFolderOptions | undefined =>
      props.paginated && {
        ...props.paginated,
        onFoldersLoaded: (browsedFolders) => {
          browsedFoldersRef.current = browsedFolders;
          publishLoadedFolders();
        },
      },
    [props.paginated, publishLoadedFolders]
  );

  const paginatedTree = usePaginatedFolderTree(paginatedOptions);
  const mode = getFolderTreeMode(props.paginated, trimmedQuery);
  const previousModeRef = useRef<FolderTreeMode | null>(null);
  const {sortByLoadOrder, syncBrowsePropsFromCache} = paginatedTree;
  const createdFolders = props.createdFolders;
  // A created folder has no position among loaded pages, and a later page may still return it. Keeping it
  // first among its siblings stops it from moving down, or jumping, as more pages load. Starred projects
  // stay above it, as in the eager picker; the server returns them before other top-level folders.
  const sortWithCreatedFoldersFirst = useCallback(
    (items: Array<TreeItem>): Array<TreeItem> => {
      const createdOrderById = new Map(
        (createdFolders ?? []).map((folder, index) => [folder.api_identifier, index])
      );
      const createdItems = items
        .filter((item) => createdOrderById.has(item.id))
        .sort((a, b) => createdOrderById.get(a.id)! - createdOrderById.get(b.id)!);
      const loadedItems = sortByLoadOrder(items.filter((item) => !createdOrderById.has(item.id)));
      return [
        ...loadedItems.filter((item) => item.forceToTop),
        ...createdItems,
        ...loadedItems.filter((item) => !item.forceToTop),
      ];
    },
    [createdFolders, sortByLoadOrder]
  );

  useEffect(() => {
    const previousMode = previousModeRef.current;
    previousModeRef.current = mode;
    // Expanding a folder loads its children into the cache, but not into the browse tree props that
    // SelectTree reads. Search uses a separate folder list, so we only need to catch up when the user
    // clears the filter and browse mode remounts the tree.
    if (previousMode === 'paginated-search' && mode === 'paginated-browse') {
      syncBrowsePropsFromCache();
    }
  }, [mode, syncBrowsePropsFromCache]);

  useEffect(() => {
    // Wait for the debounced query to catch up, so returning to search mode never repeats an earlier query.
    if (
      !searchesOnServer ||
      mode !== 'paginated-search' ||
      paginatedLevel == null ||
      debouncedQuery === '' ||
      debouncedQuery !== trimmedQuery
    ) {
      return;
    }
    // Responses for an earlier query are dropped, so a slow search cannot replace a newer one.
    let canceled = false;
    // One extra match tells us the results were cut off without counting every match.
    FolderDAO.search({
      level: paginatedLevel,
      query: debouncedQuery,
      limit: FOLDER_SEARCH_LIMIT + 1,
      showInventory,
      showNotebook,
    })
      .then((matches) => {
        if (!canceled) {
          for (const folder of matches) {
            searchedFoldersByIdRef.current.set(folder.api_identifier, folder);
          }
          setSearchResult({query: debouncedQuery, folders: matches});
          publishLoadedFolders();
        }
      })
      .catch((error) => {
        if (!canceled) {
          setSearchResult({query: debouncedQuery, folders: []});
          notifications.notifyError(error);
        }
      });
    return () => {
      canceled = true;
    };
  }, [
    debouncedQuery,
    mode,
    paginatedLevel,
    publishLoadedFolders,
    searchesOnServer,
    showInventory,
    showNotebook,
    trimmedQuery,
  ]);

  useEffect(() => {
    if (
      searchesOnServer ||
      mode !== 'paginated-search' ||
      completeFolderList != null ||
      paginatedLevel == null
    ) {
      return;
    }
    let canceled = false;
    FolderDAO.fetchAll({level: paginatedLevel})
      .done((loadedFolders: Array<MinimalFolder>) => {
        if (!canceled) {
          for (const folder of loadedFolders) {
            searchedFoldersByIdRef.current.set(folder.api_identifier, folder);
          }
          setCompleteFolderList(loadedFolders);
          publishLoadedFolders();
        }
      })
      .fail((error) => {
        if (!canceled) {
          setCompleteFolderList([]);
          notifications.notifyError(error);
        }
      });
    return () => {
      canceled = true;
    };
  }, [completeFolderList, mode, paginatedLevel, publishLoadedFolders, searchesOnServer]);

  let folders = props.folders ?? [];
  if (!includeHiddenProjects) {
    folders = folders.filter(({source}) => source && !source.hidden && !source.archived);
  }
  const hasRegistries = folders.some((folder) => RegistryUtil.isRegistry(folder.source));
  let items: Array<TreeItem>;
  let baseLeafItems: Array<TreeItem> | undefined;
  let treeQuery: string | undefined;
  let treeIsLoading: boolean | undefined;
  let isSearchTruncated = false;
  switch (mode) {
    case 'eager':
      // Existing callers provide the complete folder list, which SelectTree filters locally.
      items = FileSelectorUtil.foldersToSelectTreeItems(folders);
      baseLeafItems = undefined;
      treeQuery = debouncedQuery;
      treeIsLoading = debouncedQuery !== trimmedQuery;
      break;
    case 'paginated-browse': {
      // Fetch top-level pages and load each folder's children only when that folder is expanded.
      const createdFolderTreeItems = getCreatedFolderTreeItems(folders, props.createdFolders ?? []);
      items = mergeTreeItems(paginatedTree.items, createdFolderTreeItems.ancestorItems);
      baseLeafItems = mergeTreeItems(
        paginatedTree.baseLeafItems.filter((item) => !createdFolderTreeItems.ancestorIds.has(item.id)),
        createdFolderTreeItems.leafItems
      );
      treeQuery = undefined;
      treeIsLoading = undefined;
      break;
    }
    case 'paginated-search': {
      const shouldShowSearchFolder = FileSelectorUtil.folderPickerPredicate({
        level: props.paginated?.level ?? 'read',
        showInventory,
        showNotebook,
      });
      if (!searchesOnServer) {
        // SelectTree filters the complete list locally, as the eager picker does.
        items = FileSelectorUtil.foldersToSelectTreeItems(
          (completeFolderList ?? []).filter((folder) => shouldShowSearchFolder(folder))
        );
        baseLeafItems = undefined;
        treeQuery = debouncedQuery;
        treeIsLoading = completeFolderList == null || debouncedQuery !== trimmedQuery;
        break;
      }
      // The server returns only matching folders, without their ancestors, so each match is a top-level item.
      const isCurrentResult = searchResult?.query === trimmedQuery;
      const matches = isCurrentResult ? searchResult.folders : [];
      isSearchTruncated = matches.length > FOLDER_SEARCH_LIMIT;
      items = FileSelectorUtil.searchResultsToSelectTreeItems(
        matches.slice(0, FOLDER_SEARCH_LIMIT).filter((folder) => shouldShowSearchFolder(folder))
      );
      baseLeafItems = undefined;
      treeQuery = undefined;
      treeIsLoading = !isCurrentResult;
      break;
    }
    default:
      throw new Error(`Unknown folder tree mode: ${assertNever(mode)}`);
  }
  const paginatedBrowseTree = mode === 'paginated-browse' ? paginatedTree : undefined;
  return (
    <div>
      <TextInput
        value={query}
        placeholder="Filter..."
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        size={Size.SMALL}
      />
      <SelectTree
        // Search and browse supply different tree shapes, so their pagination state cannot be shared safely.
        key={mode}
        allowNone={allowNone}
        className={props.className}
        items={items}
        autoLoadMore={paginatedBrowseTree != null}
        baseLeafItems={baseLeafItems}
        isLoadedItemExpandable={paginatedBrowseTree?.isLoadedItemExpandable}
        multiple={props.multiple}
        itemTypeName="folders"
        selectAllText={hasRegistries ? 'All Projects and Registries' : 'All Projects'}
        selectedIds={props.folderIds}
        onChangeSelectedIds={props.onChangeSelectedFolders}
        submitHandler={props.submitHandler}
        shouldExpandNewItems={false}
        shouldExpandOnSelect={paginatedBrowseTree != null}
        shouldNotLoadMoreOnMount={mode !== 'eager'}
        loadMore={paginatedBrowseTree?.loadMore}
        moreRootItems={paginatedBrowseTree?.moreRootItems ?? false}
        isLoadingRootItems={paginatedBrowseTree?.isLoadingRootItems ?? false}
        onLoadMoreRootItems={paginatedBrowseTree?.onLoadMoreRootItems}
        // The paged endpoint returns each level in display order. Keeping that order, instead of re-sorting
        // or grouping folders with children first, lets later pages append below folders already shown.
        siblingSortFn={paginatedBrowseTree != null ? sortWithCreatedFoldersFirst : undefined}
        query={treeQuery}
        isLoading={treeIsLoading}
      />
      {isSearchTruncated && (
        <Text size="sm" color="dimmed">
          {`Showing the first ${FOLDER_SEARCH_LIMIT} matching folders. Refine the filter to see more.`}
        </Text>
      )}
    </div>
  );
}
